(() => {
  'use strict';

  if (window.MajWishlist) return;

  const STORAGE_KEY = 'maj_wishlist_handles_v1';
  const LEGACY_KEYS = ['maj_products_simple_wishlist', 'onhow-wishlist'];
  const BUTTON_SELECTOR = '[data-maj-wishlist], [data-maj-rec-wishlist], .onhow-wishlist-toggle';
  const COUNT_SELECTOR = '[data-maj-wishlist-count], .onhow-wishlist-count';
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,254}$/i;

  const unique = (values) => [...new Set(values)];

  function normalizeHandle(value) {
    if (typeof value !== 'string') return '';
    const candidate = value.trim();

    if (HANDLE_PATTERN.test(candidate)) return candidate.toLowerCase();

    try {
      const url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      const match = url.pathname.match(/^\/products\/([^/?#]+)\/?$/i);
      const handle = match ? decodeURIComponent(match[1]) : '';
      return HANDLE_PATTERN.test(handle) ? handle.toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  function handlesFromValue(value) {
    if (!Array.isArray(value)) return [];

    return value
      .map((item) => {
        if (typeof item === 'string') return normalizeHandle(item);
        if (!item || typeof item !== 'object') return '';
        return normalizeHandle(item.handle || item.productHandle || item.url || '');
      })
      .filter(Boolean);
  }

  function readKey(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || '[]');
    } catch (error) {
      return [];
    }
  }

  function write(handles) {
    const safeHandles = unique(handles.map(normalizeHandle).filter(Boolean));

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safeHandles));
      return safeHandles;
    } catch (error) {
      return null;
    }
  }

  function migrate() {
    const canonical = handlesFromValue(readKey(STORAGE_KEY));
    const legacy = LEGACY_KEYS.flatMap((key) => handlesFromValue(readKey(key)));
    const candidates = unique([...canonical, ...legacy]);
    const stored = write(candidates);
    const merged = stored || candidates;

    if (stored) {
      for (const key of LEGACY_KEYS) {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          // Storage may be blocked. The wishlist still works for the current page.
        }
      }
    }

    return merged;
  }

  function get() {
    return handlesFromValue(readKey(STORAGE_KEY));
  }

  function getButtonHandle(button) {
    return normalizeHandle(
      button.dataset.majWishlist ||
        button.dataset.majRecWishlist ||
        button.dataset.productHandle ||
        button.dataset.productUrl ||
        ''
    );
  }

  function updateButton(button, handles) {
    const handle = getButtonHandle(button);
    if (!handle) {
      button.disabled = true;
      return;
    }

    const active = handles.includes(handle);
    const productTitle = button.dataset.majWishlistTitle || button.dataset.productTitle || 'product';

    button.disabled = false;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      active ? `Remove ${productTitle} from wishlist` : `Add ${productTitle} to wishlist`
    );
    button.title = active ? 'Remove from wishlist' : 'Add to wishlist';
    button.classList.toggle('onhow-in-wishlist', active);

    const legacyIcon = button.querySelector('.onhow-wishlist-icon');
    const legacyText = button.querySelector('.onhow-wishlist-text');
    legacyIcon?.classList.toggle('onhow-wishlist-active', active);
    if (legacyText) legacyText.textContent = active ? 'In Wishlist' : 'Add to Wishlist';
  }

  function updateCounts(handles) {
    const count = handles.length;

    document.querySelectorAll(COUNT_SELECTOR).forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
      badge.style.display = count > 0 ? 'inline-flex' : 'none';
      badge.setAttribute('aria-hidden', count > 0 ? 'false' : 'true');

      const link = badge.closest('a');
      if (link) link.setAttribute('aria-label', count > 0 ? `Wishlist, ${count} items` : 'Wishlist');
    });
  }

  function refresh(root = document) {
    const handles = get();
    const buttons = [];

    if (root instanceof Element && root.matches(BUTTON_SELECTOR)) buttons.push(root);
    root.querySelectorAll?.(BUTTON_SELECTOR).forEach((button) => buttons.push(button));
    buttons.forEach((button) => updateButton(button, handles));
    updateCounts(handles);

    return handles;
  }

  function emit(handles) {
    refresh(document);
    document.dispatchEvent(
      new CustomEvent('maj:wishlist-change', {
        detail: { handles: [...handles], count: handles.length },
      })
    );
  }

  function set(handles) {
    const saved = write(handles) || unique(handles.map(normalizeHandle).filter(Boolean));
    emit(saved);
    return saved;
  }

  function toggle(handle) {
    const safeHandle = normalizeHandle(handle);
    if (!safeHandle) return get();

    const handles = get();
    return set(
      handles.includes(safeHandle)
        ? handles.filter((item) => item !== safeHandle)
        : [...handles, safeHandle]
    );
  }

  function remove(handle) {
    const safeHandle = normalizeHandle(handle);
    return set(get().filter((item) => item !== safeHandle));
  }

  function init() {
    migrate();
    refresh(document);

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest(BUTTON_SELECTOR);
      if (!button) return;

      const handle = getButtonHandle(button);
      if (!handle) return;

      event.preventDefault();
      toggle(handle);
    });

    document.addEventListener('shopify:section:load', (event) => refresh(event.target));

    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY || LEGACY_KEYS.includes(event.key)) {
        migrate();
        emit(get());
      }
    });

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR)) refresh(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.MajWishlist = {
    key: STORAGE_KEY,
    get,
    set,
    toggle,
    remove,
    refresh,
    normalizeHandle,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
